import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocations } from '@/hooks/useData';
import { RefreshCw, MapPin, Wifi } from 'lucide-react';

const demoLocations = [
  {
    id: 'BGH-K',
    name: 'بغداد - الكرخ',
    code: 'BGH-K',
    type: 'حضري',
    typeColor: '#60a5fa',
    activeSubscriptions: 450,
    coverageAreas: 'الغزالية، العامرية، الدورة، الكراده',
  },
  {
    id: 'BGH-R',
    name: 'بغداد - الرصافة',
    code: 'BGH-R',
    type: 'حضري',
    typeColor: '#60a5fa',
    activeSubscriptions: 380,
    coverageAreas: 'المنصور، الكسرة، بغداد الجديدة، الشعلة',
  },
  {
    id: 'BSR-C',
    name: 'البصرة',
    code: 'BSR-C',
    type: 'حضري',
    typeColor: '#60a5fa',
    activeSubscriptions: 210,
    coverageAreas: 'المركز، الزبير، أبو الخصيب',
  },
  {
    id: 'MOS-C',
    name: 'الموصل',
    code: 'MOS-C',
    type: 'حضري',
    typeColor: '#60a5fa',
    activeSubscriptions: 185,
    coverageAreas: 'نينوى، الحمدانية، الموصل الجديدة',
  },
  {
    id: 'KRK-C',
    name: 'كركوك',
    code: 'KRK-C',
    type: 'مختلط',
    typeColor: '#fbbf24',
    activeSubscriptions: 120,
    coverageAreas: 'المركز، الحويجة، الدبس',
  },
  {
    id: 'ERB-C',
    name: 'أربيل',
    code: 'ERB-C',
    type: 'حضري',
    typeColor: '#60a5fa',
    activeSubscriptions: 95,
    coverageAreas: 'المركز، قضاء كوية، شقلاوة',
  },
  {
    id: 'NJF-C',
    name: 'النجف',
    code: 'NJF-C',
    type: 'حضري',
    typeColor: '#60a5fa',
    activeSubscriptions: 87,
    coverageAreas: 'النجف الأشرف، الكوفة، المشخاب',
  },
  {
    id: 'KBL-C',
    name: 'كربلاء',
    code: 'KBL-C',
    type: 'حضري',
    typeColor: '#60a5fa',
    activeSubscriptions: 76,
    coverageAreas: 'كربلاء المقدسة، الهندية، الحسينية',
  },
  {
    id: 'HLL-C',
    name: 'الحلة - بابل',
    code: 'HLL-C',
    type: 'حضري',
    typeColor: '#60a5fa',
    activeSubscriptions: 68,
    coverageAreas: 'الحلة، المحاويل، المسيب',
  },
];

const LocationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: rawData, isLoading, isFetching, refetch } = useLocations();

  const locations = React.useMemo(() => {
    const items = (rawData as any)?.items || [];
    if (items.length === 0) return demoLocations;

    return items.map((item: any) => {
      const code = item.self?.displayValue || item.self?.id || '';
      const demoMatch = demoLocations.find(d => d.code === code || d.id === code);
      if (demoMatch) {
        return {
          ...demoMatch,
          id: item.self?.id || demoMatch.id,
          name: item.self?.displayValue || demoMatch.name,
        };
      }
      return {
        id: item.self?.id || 'UNKNOWN',
        name: `المنطقة ${item.self?.displayValue || item.self?.id || ''}`,
        code: item.self?.displayValue || item.self?.id || '',
        type: 'حضري',
        typeColor: '#60a5fa',
        activeSubscriptions: 0,
        coverageAreas: 'منطقة مغطاة نشطة بالشبكة الوطنية',
      };
    });
  }, [rawData]);

  return (
    <div style={{ padding: '20px', direction: 'rtl', minHeight: '100%' }}>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}
      >
        <div style={{ textAlign: 'right' }}>
          <h1 style={{ color: '#e2e8f0', fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>
            {t('locations.title') || 'المناطق'}
          </h1>
          <p style={{ color: '#64748b', fontSize: '13px' }}>
            {t('common.total') || 'الإجمالي'} {locations.length}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '8px',
            padding: '8px 14px',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          <RefreshCw size={14} style={isFetching ? { animation: 'spin 1s linear infinite' } : {}} />
          {t('common.refresh') || 'تحديث'}
        </button>
      </div>

      {/* Locations grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
          جاري التحميل...
        </div>
      ) : (
        <div
          className="locations-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
          }}
        >
          {locations.map((loc: any) => (
            <div
              key={loc.id}
              style={{
                background: 'rgba(13,31,60,0.7)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '12px',
                padding: '20px',
                direction: 'rtl',
                transition: 'border-color 0.2s',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(59,130,246,0.4)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)';
              }}
            >
              {/* Top row: type badge + location pin + name */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                <MapPin size={18} color="#3b82f6" style={{ marginTop: '2px', flexShrink: 0 }} />
                <div style={{ textAlign: 'right', flex: 1, marginRight: '8px' }}>
                  <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '16px', marginBottom: '2px' }}>
                    {loc.name}
                  </div>
                  <div style={{ color: '#475569', fontSize: '12px', direction: 'ltr', textAlign: 'right' }}>
                    {loc.code}
                  </div>
                </div>
                <span
                  style={{
                    background: loc.type === 'مختلط' ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)',
                    color: loc.type === 'مختلط' ? '#fbbf24' : '#60a5fa',
                    border: `1px solid ${loc.type === 'مختلط' ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.3)'}`,
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {loc.type}
                </span>
              </div>

              {/* Active subscriptions */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end', marginBottom: '4px' }}>
                  <span style={{ color: '#64748b', fontSize: '12px' }}>
                    {t('locations.activeSubscriptions') || 'الاشتراكات النشطة'}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ color: '#e2e8f0', fontSize: '18px', fontWeight: 700 }}>{loc.activeSubscriptions}</span>
                    <Wifi size={16} color="#34d399" />
                  </div>
                </div>
              </div>

              {/* Coverage areas */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '10px' }}>
                <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '4px', textAlign: 'right' }}>
                  {t('locations.coverageAreas') || 'نطاق التغطية'}
                </div>
                <div style={{ color: '#94a3b8', fontSize: '12px', textAlign: 'right', lineHeight: 1.5 }}>
                  {loc.coverageAreas}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationsPage;